import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { isVoucherPayMode, isWalletPayMode } from '../../util/constants';

export type WalletTender = {
  client_id: number;
  client?: string;
  balance: number;
  is_active: boolean;
};

export const isUsableWalletTender = (wallet?: WalletTender | null): boolean =>
  Boolean(wallet?.is_active);

export const filterPayModesForWallet = <T extends { id: number | string }>(
  modes: readonly T[],
  showWallet: boolean,
): T[] =>
  modes.filter((mode) => {
    if (isVoucherPayMode(mode.id)) return false;
    if (!showWallet && isWalletPayMode(mode.id)) return false;
    return true;
  });

export const walletTenderCap = (wallet?: WalletTender | null): number =>
  isUsableWalletTender(wallet) ? Math.max(0, Number(wallet?.balance || 0)) : 0;

export const useWalletTender = ({
  clientId,
  clientName,
  enabled = true,
}: {
  clientId?: number | string | null;
  clientName?: string | null;
  enabled?: boolean;
}) => {
  const { api } = useAuth();
  const { queueReRun } = useUI();
  const [wallet, setWallet] = useState<WalletTender | null>(null);
  const [loading, setLoading] = useState(false);
  const numericId = clientId == null || clientId === '' ? null : Number(clientId);
  const hasId = Number.isFinite(numericId) && (numericId as number) > 0;
  const name = String(clientName || '').trim();

  const fetchTender = async () => {
    if (!enabled || (!hasId && !name)) {
      setWallet(null);
      return;
    }
    setLoading(true);
    const res = await api.get('/wallets/tender', {
      params: hasId ? { Client: numericId } : { Name: name },
      skipErrorToast: true,
    } as Parameters<typeof api.get>[1]);
    if (res?.status === 200) {
      setWallet(res.data?.exists === false ? null : res.data);
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(fetchTender);
    } else {
      setWallet(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    void fetchTender();
  }, [enabled, hasId, numericId, name]);

  return { wallet, loading, refresh: fetchTender };
};
